import React, { useEffect, useState, useRef } from "react";
import api, { fcfa } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MapPin, Store, Package, Plus, Search, Camera, Check,
  UtensilsCrossed, BedDouble, Car, Wrench, ChevronRight,
  Clock, Phone, User, ArrowLeft, Upload, X, RefreshCw, Pencil,
} from "lucide-react";

// ─── Constantes ──────────────────────────────────────────────────────────────

const VERTICALS = [
  { key: "shop",        label: "Boutique",      icon: Store,          color: "bg-blue-500" },
  { key: "restaurant",  label: "Restaurant",    icon: UtensilsCrossed, color: "bg-orange-500" },
  { key: "hebergement", label: "Hébergement",   icon: BedDouble,      color: "bg-purple-500" },
  { key: "transport",   label: "Transport",     icon: Car,            color: "bg-green-500" },
  { key: "provider",    label: "Prestataire",   icon: Wrench,         color: "bg-teal-500" },
];

const TYPE_LABEL = {
  shop: "Boutique",
  restaurant: "Restaurant",
  hebergement: "Hébergement",
  transport: "Transport",
  provider: "Prestataire",
};

const PRICE_RANGES = ["economique", "moyen", "premium"];
const TRANSPORT_TYPES = ["taxi", "bus", "mototaxi", "navette", "location"];
const HEBERGEMENT_TYPES = ["hotel", "auberge", "residence", "villa", "chambre"];

// ─── Composant principal ──────────────────────────────────────────────────────

export default function AgentDashboard() {
  const [view, setView] = useState("home"); // home | new | shops | visits | addproduct
  const [vertical, setVertical] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [visits, setVisits] = useState([]);
  const [nearbyShops, setNearbyShops] = useState([]);
  const [allShops, setAllShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);
  const [editEntity, setEditEntity] = useState(null); // { type, id, data }
  const [stats, setStats] = useState({ shops: 0, providers: 0, visits: 0, products: 0 });

  const loadVisits = () => api.get("/agent/visits").then(r => setVisits(r.data)).catch(() => {});
  const loadStats = () => api.get("/agent/stats").then(r => setStats(r.data)).catch(() => {});

  useEffect(() => { loadVisits(); loadStats(); }, []);

  const getGps = () => {
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
      () => { toast.error("GPS non disponible"); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const loadNearby = async () => {
    if (!gps) { getGps(); return; }
    const r = await api.get(`/agent/shops/nearby?lat=${gps.lat}&lng=${gps.lng}&radius=5`);
    setNearbyShops(r.data);
  };

  const loadAllShops = async (q = "") => {
    const r = await api.get(`/agent/shops?q=${encodeURIComponent(q)}`);
    setAllShops(r.data);
  };

  const openEdit = async (entityType, entityId) => {
    try {
      const r = await api.get(`/agent/entity/${entityType}/${entityId}`);
      setEditEntity({ type: entityType, id: entityId, data: r.data });
      setView("edit");
    } catch { toast.error("Impossible de charger les données"); }
  };

  const openShop = async (shop) => {
    setSelectedShop(shop);
    const r = await api.get(`/agent/shops/${shop.shop_id}/products`);
    setShopProducts(r.data);
    setView("shopdetail");
  };

  // ── HOME ──────────────────────────────────────────────────────────────────
  if (view === "home") return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-24">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-xl font-bold text-secondary">Espace Agent</h1>
          <p className="text-xs text-muted-foreground">LesMeilleursPrix — Collecte terrain</p>
        </div>
        {gps
          ? <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />GPS actif</Badge>
          : <Button size="sm" variant="outline" className="text-xs h-7" onClick={getGps} disabled={gpsLoading}>
              {gpsLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}GPS
            </Button>
        }
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[
          { label: "Boutiques", value: stats.shops, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Prestataires", value: stats.providers, color: "text-teal-600", bg: "bg-teal-50" },
          { label: "Visites", value: stats.visits, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Produits", value: stats.products, color: "text-green-600", bg: "bg-green-50" },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
            <div className={`text-2xl font-bold font-display ${s.color}`}>{s.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <ActionCard icon={Plus} label="Nouvel établissement" color="bg-primary" onClick={() => setView("new")} />
        <ActionCard icon={Store} label="Mes boutiques" color="bg-blue-500" onClick={() => { loadAllShops(); setView("shops"); }} />
        <ActionCard icon={MapPin} label="Autour de moi" color="bg-green-500" onClick={() => { loadNearby(); setView("nearby"); }} />
        <ActionCard icon={Clock} label="Historique visites" color="bg-amber-500" onClick={() => { loadVisits(); setView("visits"); }} />
      </div>

      {/* Stats rapides */}
      <div className="bg-card border border-border rounded-xl p-4 mb-4">
        <div className="text-xs font-semibold text-muted-foreground mb-3">Mes visites récentes</div>
        {visits.length === 0
          ? <p className="text-sm text-muted-foreground text-center py-4">Aucune visite enregistrée</p>
          : visits.slice(0, 5).map(v => (
            <div key={v.visit_id} className="flex items-center gap-3 py-2 border-t border-border first:border-0">
              <VerticalIcon type={v.entity_type} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-secondary truncate">{v.entity_name || v.entity_id}</div>
                <div className="text-xs text-muted-foreground">{new Date(v.visited_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge variant="secondary" className="text-xs">{TYPE_LABEL[v.entity_type] || v.entity_type}</Badge>
                <button onClick={() => openEdit(v.entity_type, v.entity_id)}
                  className="text-xs text-primary flex items-center gap-0.5 hover:underline">
                  <Pencil className="w-3 h-3" />Modifier
                </button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );

  // ── CHOIX VERTICAL ────────────────────────────────────────────────────────
  if (view === "new") return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <BackBtn onClick={() => setView("home")} />
      <h2 className="font-display text-lg font-bold text-secondary mb-1">Nouvel enregistrement</h2>
      <p className="text-sm text-muted-foreground mb-5">Quel type d'établissement ?</p>
      <div className="space-y-3">
        {VERTICALS.map(v => (
          <button key={v.key} onClick={() => { setVertical(v.key); setView("form"); }}
            className="w-full flex items-center gap-4 bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:shadow-sm transition-all text-left">
            <div className={`${v.color} rounded-xl p-2.5`}>
              <v.icon className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-secondary">{v.label}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
          </button>
        ))}
      </div>
    </div>
  );

  // ── FORMULAIRE ENREGISTREMENT ─────────────────────────────────────────────
  if (view === "form") return (
    <FormView vertical={vertical} gps={gps} getGps={getGps} gpsLoading={gpsLoading}
      onBack={() => setView("new")}
      onSuccess={() => { loadVisits(); setView("home"); toast.success("Enregistré avec succès !"); }}
    />
  );

  // ── LISTE BOUTIQUES ───────────────────────────────────────────────────────
  if (view === "shops") return (
    <ShopsListView shops={allShops} onSearch={loadAllShops} onSelect={openShop} onBack={() => setView("home")} />
  );

  // ── BOUTIQUES PROCHES ─────────────────────────────────────────────────────
  if (view === "nearby") return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <BackBtn onClick={() => setView("home")} />
      <h2 className="font-display text-lg font-bold text-secondary mb-4">Autour de moi</h2>
      {nearbyShops.length === 0
        ? <div className="text-center py-10 text-muted-foreground text-sm">
            {gps ? "Aucune boutique à proximité." : "Activez le GPS d'abord."}
          </div>
        : nearbyShops.map(s => (
          <ShopCard key={s.shop_id} shop={s} onClick={() => openShop(s)} showDistance />
        ))
      }
    </div>
  );

  // ── DÉTAIL BOUTIQUE + PRODUITS ────────────────────────────────────────────
  if (view === "shopdetail" && selectedShop) return (
    <ShopDetailView
      shop={selectedShop} products={shopProducts} gps={gps}
      onBack={() => setView("shops")}
      onProductAdded={() => api.get(`/agent/shops/${selectedShop.shop_id}/products`).then(r => setShopProducts(r.data))}
      onVisitLogged={loadVisits}
    />
  );

  // ── ÉDITION ÉTABLISSEMENT ─────────────────────────────────────────────────
  if (view === "edit" && editEntity) return (
    <EditEntityView
      entity={editEntity}
      gps={gps} getGps={getGps} gpsLoading={gpsLoading}
      onBack={() => setView("visits")}
      onSuccess={() => { loadVisits(); setView("visits"); toast.success("Informations mises à jour !"); }}
    />
  );

  // ── HISTORIQUE VISITES ────────────────────────────────────────────────────
  if (view === "visits") return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <BackBtn onClick={() => setView("home")} />
      <h2 className="font-display text-lg font-bold text-secondary mb-4">Historique des visites ({visits.length})</h2>
      {visits.length === 0
        ? <p className="text-center py-10 text-sm text-muted-foreground">Aucune visite enregistrée.</p>
        : visits.map(v => (
          <div key={v.visit_id} className="bg-card border border-border rounded-xl p-4 mb-2">
            <div className="flex items-start gap-3">
              <VerticalIcon type={v.entity_type} big />
              <div className="flex-1">
                <div className="font-medium text-secondary">{v.entity_name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {new Date(v.visited_at).toLocaleDateString("fr-FR", { weekday:"short", day:"2-digit", month:"long", hour:"2-digit", minute:"2-digit" })}
                </div>
                {v.contact_name && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><User className="w-3 h-3" />{v.contact_name}</div>}
                {v.contact_phone && <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{v.contact_phone}</div>}
                {v.notes && <div className="text-xs bg-muted rounded p-2 mt-2">{v.notes}</div>}
                {v.lat && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{v.lat.toFixed(5)}, {v.lng.toFixed(5)}</div>}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <Badge variant="secondary" className="text-xs">{TYPE_LABEL[v.entity_type] || v.entity_type}</Badge>
                <button onClick={() => openEdit(v.entity_type, v.entity_id)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Pencil className="w-3 h-3" />Modifier
                </button>
              </div>
            </div>
          </div>
        ))
      }
    </div>
  );

  return null;
}

// ─── Formulaire universel ─────────────────────────────────────────────────────

function FormView({ vertical, gps, getGps, gpsLoading, onBack, onSuccess }) {
  const vInfo = VERTICALS.find(v => v.key === vertical);
  const [form, setForm] = useState({ name:"", description:"", city:"Abidjan", address:"", phone:"", contact_name:"",
    contact_email:"", contact_whatsapp:"",
    cuisine:"", price_range:"moyen", hours:"", htype:"hotel", email:"", amenities:[], price_from:"",
    ttype:"taxi", zones:"", tarif_base:"", tarif_km:"", profession:"", services:"", base_rate:"", notes:"" });
  const [useGps, setUseGps] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async () => {
    if (!imageFile) return "";
    const fd = new FormData();
    fd.append("file", imageFile);
    const r = await api.post("/agent/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom de l'établissement est obligatoire"); return; }
    if (!form.contact_name.trim()) { toast.error("Le nom du responsable est obligatoire"); return; }
    if (!form.phone.trim()) { toast.error("Le téléphone du responsable est obligatoire"); return; }
    setBusy(true);
    try {
      let imageUrl = "";
      if (imageFile) imageUrl = await uploadImage();
      const lat = useGps && gps ? gps.lat : null;
      const lng = useGps && gps ? gps.lng : null;

      const ownerInfo = {
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_whatsapp: form.contact_whatsapp,
      };
      if (vertical === "shop") {
        await api.post("/agent/shops", { ...form, ...ownerInfo, lat, lng, image_url: imageUrl });
      } else {
        await api.post("/agent/providers", {
          ...form, ...ownerInfo,
          vertical,
          lat, lng,
          amenities: form.amenities,
          zones: form.zones ? form.zones.split(",").map(s => s.trim()) : [],
          services: form.services ? form.services.split(",").map(s => s.trim()) : [],
          price_from: form.price_from ? parseFloat(form.price_from) : null,
          tarif_base: form.tarif_base ? parseFloat(form.tarif_base) : null,
          tarif_km: form.tarif_km ? parseFloat(form.tarif_km) : null,
          base_rate: form.base_rate ? parseFloat(form.base_rate) : null,
          image_url: imageUrl,
        });
      }
      onSuccess();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      <BackBtn onClick={onBack} />
      <div className="flex items-center gap-3 mb-5">
        <div className={`${vInfo?.color} rounded-xl p-2`}><vInfo.icon className="w-5 h-5 text-white" /></div>
        <h2 className="font-display text-lg font-bold text-secondary">Nouveau {vInfo?.label}</h2>
      </div>

      <div className="space-y-4">
        {/* Photo */}
        <div>
          <label className="text-xs font-semibold text-muted-foreground mb-1 block">Photo</label>
          <div className="flex items-center gap-3">
            <div className="w-20 h-20 rounded-xl border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer" onClick={() => fileRef.current?.click()}>
              {imagePreview ? <img src={imagePreview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-7 h-7 text-muted-foreground/40" />}
            </div>
            <div>
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="text-xs">
                <Upload className="w-3.5 h-3.5 mr-1" />Choisir une photo
              </Button>
              {imageFile && <p className="text-xs text-muted-foreground mt-1">{imageFile.name}</p>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
          </div>
        </div>

        <Field label="Nom *" value={form.name} onChange={v => set("name", v)} placeholder={`Nom du ${vInfo?.label}`} />
        <Field label="Description" value={form.description} onChange={v => set("description", v)} multiline />

        {/* Champs spécifiques vertical */}
        {vertical === "restaurant" && <>
          <Field label="Cuisine" value={form.cuisine} onChange={v => set("cuisine", v)} placeholder="Ivoirienne, Française…" />
          <FieldSelect label="Gamme de prix" value={form.price_range} onChange={v => set("price_range", v)} options={PRICE_RANGES} />
          <Field label="Horaires" value={form.hours} onChange={v => set("hours", v)} placeholder="Lun-Dim 10h-23h" />
        </>}

        {vertical === "hebergement" && <>
          <FieldSelect label="Type" value={form.htype} onChange={v => set("htype", v)} options={HEBERGEMENT_TYPES} />
          <Field label="Email" value={form.email} onChange={v => set("email", v)} type="email" />
          <Field label="Prix à partir de (FCFA/nuit)" value={form.price_from} onChange={v => set("price_from", v)} type="number" />
          <Field label="Équipements (séparés par virgule)" value={form.amenities.join(",")} onChange={v => set("amenities", v.split(",").map(s=>s.trim()).filter(Boolean))} placeholder="wifi, piscine, parking…" />
        </>}

        {vertical === "transport" && <>
          <FieldSelect label="Type" value={form.ttype} onChange={v => set("ttype", v)} options={TRANSPORT_TYPES} />
          <Field label="Tarif de base (FCFA)" value={form.tarif_base} onChange={v => set("tarif_base", v)} type="number" />
          <Field label="Tarif au km (FCFA)" value={form.tarif_km} onChange={v => set("tarif_km", v)} type="number" />
          <Field label="Zones desservies (séparées par virgule)" value={form.zones} onChange={v => set("zones", v)} placeholder="Cocody, Plateau, Adjamé…" />
        </>}

        {vertical === "provider" && <>
          <Field label="Profession / Domaine" value={form.profession} onChange={v => set("profession", v)} placeholder="Plombier, Coiffeur…" />
          <Field label="Services (séparés par virgule)" value={form.services} onChange={v => set("services", v)} placeholder="Installation, Réparation…" />
          <Field label="Tarif de base (FCFA)" value={form.base_rate} onChange={v => set("base_rate", v)} type="number" />
          <Field label="Zones / Quartiers" value={form.zones} onChange={v => set("zones", v)} placeholder="Cocody, Plateau…" />
        </>}

        {/* Champs communs */}
        <Field label="Ville" value={form.city} onChange={v => set("city", v)} />
        <Field label="Adresse" value={form.address} onChange={v => set("address", v)} placeholder="Rue, quartier…" />

        {/* Responsable */}
        <div className="pt-2 pb-1 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Responsable / Propriétaire</p>
          <div className="space-y-3">
            <Field label="Nom du responsable *" value={form.contact_name} onChange={v => set("contact_name", v)} placeholder="Nom complet" />
            <Field label="Téléphone *" value={form.phone} onChange={v => set("phone", v)} type="tel" placeholder="+225 07 00 00 00 00" />
            <Field label="WhatsApp" value={form.contact_whatsapp} onChange={v => set("contact_whatsapp", v)} type="tel" placeholder="+225 07 00 00 00 00 (si différent)" />
            <Field label="Email" value={form.contact_email} onChange={v => set("contact_email", v)} type="email" placeholder="responsable@exemple.ci" />
          </div>
        </div>

        <Field label="Notes de visite" value={form.notes} onChange={v => set("notes", v)} multiline placeholder="Observations, horaires atypiques…" />

        {/* GPS */}
        <div className="bg-muted/50 rounded-xl p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-secondary">Géolocalisation</span>
            <button onClick={() => { setUseGps(!useGps); if (!gps) getGps(); }}
              className={`relative w-10 h-5 rounded-full transition-colors ${useGps ? "bg-primary" : "bg-muted-foreground/30"}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${useGps ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          {gps && useGps
            ? <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</p>
            : <p className="text-xs text-muted-foreground mt-1">{gpsLoading ? "Localisation en cours…" : "Activez pour enregistrer la position exacte"}</p>
          }
          {!gps && <Button size="sm" variant="outline" className="mt-2 text-xs h-7" onClick={getGps} disabled={gpsLoading}>
            {gpsLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}Obtenir GPS
          </Button>}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 flex gap-3 max-w-lg mx-auto">
        <Button variant="outline" className="flex-1" onClick={onBack} disabled={busy}><X className="w-4 h-4 mr-1" />Annuler</Button>
        <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={submit} disabled={busy}>
          {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Enregistrer
        </Button>
      </div>
    </div>
  );
}

// ─── Liste boutiques ──────────────────────────────────────────────────────────

function ShopsListView({ shops, onSearch, onSelect, onBack }) {
  const [q, setQ] = useState("");
  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <BackBtn onClick={onBack} />
      <h2 className="font-display text-lg font-bold text-secondary mb-4">Boutiques</h2>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Rechercher une boutique…" value={q}
          onChange={e => { setQ(e.target.value); onSearch(e.target.value); }} />
      </div>
      {shops.length === 0
        ? <p className="text-center py-10 text-sm text-muted-foreground">Aucune boutique trouvée.</p>
        : shops.map(s => <ShopCard key={s.shop_id} shop={s} onClick={() => onSelect(s)} />)
      }
    </div>
  );
}

// ─── Détail boutique ──────────────────────────────────────────────────────────

function ShopDetailView({ shop, products, gps, onBack, onProductAdded, onVisitLogged }) {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showLogVisit, setShowLogVisit] = useState(false);
  const [newProd, setNewProd] = useState({ name:"", prix_detail:"", prix_demi_gros:"", prix_gros:"", unit:"unité", conditionnement:"", category:"", image_url:"" });
  const [editingProduct, setEditingProduct] = useState(null); // produit en cours d'édition complète
  const [visitNotes, setVisitNotes] = useState("");
  const [visitContact, setVisitContact] = useState({ name: shop.contact_name || "", phone: shop.phone || "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const editFileRef = useRef();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");

  const pickImage = e => {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
  };

  const pickEditImage = e => {
    const f = e.target.files[0];
    if (f) { setEditImageFile(f); setEditImagePreview(URL.createObjectURL(f)); }
  };

  const uploadImage = async (file) => {
    const f = file || imageFile;
    if (!f) return "";
    const fd = new FormData(); fd.append("file", f);
    const r = await api.post("/agent/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const openEditProduct = (p) => {
    setEditingProduct({
      product_id: p.product_id,
      name: p.name || "",
      category: p.category || "",
      unit: p.unit || "unité",
      conditionnement: p.conditionnement || "",
      prix_detail:    p.prix_detail    != null ? String(p.prix_detail)    : (p.price != null ? String(p.price) : ""),
      prix_demi_gros: p.prix_demi_gros != null ? String(p.prix_demi_gros) : "",
      prix_gros:      p.prix_gros      != null ? String(p.prix_gros)      : "",
      image_url: p.image_url || "",
    });
    setEditImageFile(null);
    setEditImagePreview(p.image_url || "");
  };

  const saveEditProduct = async () => {
    if (!editingProduct.name.trim()) { toast.error("Nom requis"); return; }
    setBusy(true);
    try {
      let img = editingProduct.image_url;
      if (editImageFile) img = await uploadImage(editImageFile);
      await api.put(`/agent/products/${editingProduct.product_id}`, {
        ...editingProduct,
        image_url: img,
        prix_detail:    editingProduct.prix_detail    ? parseFloat(editingProduct.prix_detail)    : null,
        prix_demi_gros: editingProduct.prix_demi_gros ? parseFloat(editingProduct.prix_demi_gros) : null,
        prix_gros:      editingProduct.prix_gros      ? parseFloat(editingProduct.prix_gros)      : null,
      });
      toast.success("Produit mis à jour");
      setEditingProduct(null); setEditImageFile(null); setEditImagePreview("");
      onProductAdded();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const addProduct = async () => {
    if (!newProd.name) { toast.error("Nom du produit requis"); return; }
    if (!newProd.prix_detail && !newProd.prix_demi_gros && !newProd.prix_gros) {
      toast.error("Au moins un prix est requis"); return;
    }
    setBusy(true);
    try {
      let img = "";
      if (imageFile) img = await uploadImage(imageFile);
      await api.post("/agent/products", {
        ...newProd,
        shop_id: shop.shop_id,
        prix_detail:    newProd.prix_detail    ? parseFloat(newProd.prix_detail)    : null,
        prix_demi_gros: newProd.prix_demi_gros ? parseFloat(newProd.prix_demi_gros) : null,
        prix_gros:      newProd.prix_gros      ? parseFloat(newProd.prix_gros)      : null,
        image_url: img,
      });
      toast.success("Produit ajouté");
      setShowAddProduct(false);
      setNewProd({ name:"", prix_detail:"", prix_demi_gros:"", prix_gros:"", unit:"unité", conditionnement:"", category:"", image_url:"" });
      setImageFile(null); setImagePreview("");
      onProductAdded();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };


  const logVisit = async () => {
    await api.post("/agent/visits", {
      entity_type: "shop", entity_id: shop.shop_id,
      lat: gps?.lat, lng: gps?.lng,
      notes: visitNotes, contact_name: visitContact.name, contact_phone: visitContact.phone,
    });
    toast.success("Visite enregistrée");
    setShowLogVisit(false);
    onVisitLogged();
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      <BackBtn onClick={onBack} />

      {/* En-tête boutique */}
      <div className="bg-card border border-border rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 rounded-xl p-2.5"><Store className="w-5 h-5 text-blue-600" /></div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-secondary">{shop.name}</h2>
            {shop.category_name && <Badge variant="secondary" className="text-xs mt-0.5">{shop.category_name}</Badge>}
            {shop.address && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{shop.address}</p>}
            {shop.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{shop.phone}</p>}
            {shop.distance_km && <p className="text-xs text-green-600 flex items-center gap-1"><MapPin className="w-3 h-3" />{shop.distance_km} km</p>}
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="flex-1 bg-primary text-white text-xs h-8" onClick={() => setShowAddProduct(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" />Ajouter produit
          </Button>
          <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => setShowLogVisit(true)}>
            <Check className="w-3.5 h-3.5 mr-1" />Enregistrer visite
          </Button>
        </div>
      </div>

      {/* Ajouter produit */}
      {showAddProduct && (
        <div className="bg-card border border-primary/30 rounded-xl p-4 mb-4 space-y-3">
          <div className="font-semibold text-secondary text-sm flex items-center gap-2"><Package className="w-4 h-4 text-primary" />Nouveau produit</div>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer" onClick={() => fileRef.current?.click()}>
              {imagePreview ? <img src={imagePreview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-6 h-6 text-muted-foreground/40" />}
            </div>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" />Photo</Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
          </div>
          <Field label="Nom du produit *" value={newProd.name} onChange={v => setNewProd(p=>({...p,name:v}))} />
          <Field label="Catégorie" value={newProd.category} onChange={v => setNewProd(p=>({...p,category:v}))} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Unité" value={newProd.unit} onChange={v => setNewProd(p=>({...p,unit:v}))} placeholder="kg, litre, carton…" />
            <Field label="Conditionnement" value={newProd.conditionnement} onChange={v => setNewProd(p=>({...p,conditionnement:v}))} placeholder="Carton 24, Sac 50kg…" />
          </div>
          {/* Niveaux de prix */}
          <div className="bg-muted/40 rounded-xl p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prix (FCFA)</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Détail</label>
                <input type="number" value={newProd.prix_detail} onChange={e=>setNewProd(p=>({...p,prix_detail:e.target.value}))}
                  placeholder="0" className="w-full border border-border rounded-lg px-2 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Demi-gros</label>
                <input type="number" value={newProd.prix_demi_gros} onChange={e=>setNewProd(p=>({...p,prix_demi_gros:e.target.value}))}
                  placeholder="0" className="w-full border border-border rounded-lg px-2 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Gros</label>
                <input type="number" value={newProd.prix_gros} onChange={e=>setNewProd(p=>({...p,prix_gros:e.target.value}))}
                  placeholder="0" className="w-full border border-border rounded-lg px-2 py-2 text-sm bg-background" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowAddProduct(false)}>Annuler</Button>
            <Button size="sm" className="flex-1 bg-primary" onClick={addProduct} disabled={busy}>{busy?"…":"Ajouter"}</Button>
          </div>
        </div>
      )}

      {/* Log visite */}
      {showLogVisit && (
        <div className="bg-card border border-green-200 rounded-xl p-4 mb-4 space-y-3">
          <div className="font-semibold text-secondary text-sm flex items-center gap-2"><Check className="w-4 h-4 text-green-600" />Enregistrer visite</div>
          <Field label="Contact (nom)" value={visitContact.name} onChange={v => setVisitContact(c=>({...c,name:v}))} />
          <Field label="Contact (téléphone)" value={visitContact.phone} onChange={v => setVisitContact(c=>({...c,phone:v}))} type="tel" />
          <Field label="Notes / Observations" value={visitNotes} onChange={setVisitNotes} multiline />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowLogVisit(false)}>Annuler</Button>
            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={logVisit}>Enregistrer</Button>
          </div>
        </div>
      )}

      {/* Liste produits */}
      <div className="font-semibold text-secondary text-sm mb-3">Produits ({products.length})</div>
      {products.length === 0
        ? <p className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-xl">Aucun produit enregistré pour cette boutique.</p>
        : products.map(p => (
          <div key={p.product_id} className="mb-2">
            {/* Formulaire d'édition complet */}
            {editingProduct?.product_id === p.product_id ? (
              <div className="bg-card border border-primary/40 rounded-xl p-4 space-y-3">
                <div className="font-semibold text-secondary text-sm flex items-center gap-2"><Pencil className="w-4 h-4 text-primary" />Modifier le produit</div>

                {/* Image */}
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer"
                    onClick={() => editFileRef.current?.click()}>
                    {editImagePreview
                      ? <img src={editImagePreview} alt="" className="w-full h-full object-cover" />
                      : <Camera className="w-6 h-6 text-muted-foreground/40" />}
                  </div>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => editFileRef.current?.click()}>
                    <Upload className="w-3.5 h-3.5 mr-1" />Changer photo
                  </Button>
                  <input ref={editFileRef} type="file" accept="image/*" className="hidden" onChange={pickEditImage} />
                </div>

                <Field label="Nom *" value={editingProduct.name} onChange={v => setEditingProduct(ep=>({...ep, name:v}))} />
                <Field label="Catégorie" value={editingProduct.category} onChange={v => setEditingProduct(ep=>({...ep, category:v}))} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Unité" value={editingProduct.unit} onChange={v => setEditingProduct(ep=>({...ep, unit:v}))} placeholder="kg, litre…" />
                  <Field label="Conditionnement" value={editingProduct.conditionnement} onChange={v => setEditingProduct(ep=>({...ep, conditionnement:v}))} placeholder="Carton 24…" />
                </div>

                {/* Prix */}
                <div className="bg-muted/40 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prix (FCFA)</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[["Détail", "prix_detail"], ["Demi-gros", "prix_demi_gros"], ["Gros", "prix_gros"]].map(([label, key]) => (
                      <div key={key}>
                        <label className="text-xs text-muted-foreground block mb-1">{label}</label>
                        <input type="number" value={editingProduct[key]}
                          onChange={e => setEditingProduct(ep=>({...ep, [key]: e.target.value}))}
                          placeholder="0" className="w-full border border-border rounded-lg px-2 py-2 text-sm bg-background" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditingProduct(null); setEditImageFile(null); setEditImagePreview(""); }}>Annuler</Button>
                  <Button size="sm" className="flex-1 bg-primary" onClick={saveEditProduct} disabled={busy}>{busy?"…":"Enregistrer"}</Button>
                </div>
              </div>
            ) : (
              /* Affichage normal */
              <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden shrink-0 flex items-center justify-center cursor-pointer"
                  onClick={() => openEditProduct(p)}>
                  {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" onError={e=>e.target.style.display="none"} /> : <Package className="w-5 h-5 text-muted-foreground/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-secondary truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{[p.unit, p.conditionnement].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="shrink-0 text-right">
                  {p.prix_detail    && <div className="text-xs"><span className="text-muted-foreground">D: </span><span className="font-semibold text-primary">{Number(p.prix_detail).toLocaleString("fr-FR")}</span></div>}
                  {p.prix_demi_gros && <div className="text-xs"><span className="text-muted-foreground">DG: </span><span className="font-semibold text-amber-600">{Number(p.prix_demi_gros).toLocaleString("fr-FR")}</span></div>}
                  {p.prix_gros      && <div className="text-xs"><span className="text-muted-foreground">G: </span><span className="font-semibold text-green-600">{Number(p.prix_gros).toLocaleString("fr-FR")}</span></div>}
                  {!p.prix_detail && !p.prix_demi_gros && !p.prix_gros && p.price && <div className="font-semibold text-primary text-sm">{Number(p.price).toLocaleString("fr-FR")}</div>}
                  <button onClick={() => openEditProduct(p)} className="text-xs text-primary flex items-center gap-0.5 mt-1 ml-auto hover:underline">
                    <Pencil className="w-3 h-3" />Modifier
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
}

// ─── Formulaire édition établissement ────────────────────────────────────────

function EditEntityView({ entity, gps, getGps, gpsLoading, onBack, onSuccess }) {
  const { type, id, data } = entity;
  const vInfo = VERTICALS.find(v => v.key === type) || VERTICALS[0];

  const [form, setForm] = useState({
    name:         data.name || data.nom || "",
    description:  data.description || data.bio || "",
    city:         data.city || data.ville || "Abidjan",
    address:      data.address || "",
    phone:        data.phone || "",
    contact_name: data.contact_name || "",
    // restaurant
    cuisine:      data.cuisine || "",
    price_range:  data.price_range || "moyen",
    hours:        data.hours || "",
    // hebergement
    htype:        data.type || "hotel",
    email:        data.email || "",
    price_from:   data.price_from ? String(data.price_from) : "",
    amenities:    Array.isArray(data.amenities) ? data.amenities.join(", ") : (data.amenities || ""),
    // transport
    ttype:        data.type || "taxi",
    zones:        Array.isArray(data.zones) ? data.zones.join(", ") : (data.zones || ""),
    tarif_base:   data.tarif_base ? String(data.tarif_base) : "",
    tarif_km:     data.tarif_km ? String(data.tarif_km) : "",
    // provider
    profession:   data.profession || "",
    services:     Array.isArray(data.services) ? data.services.join(", ") : (data.services || ""),
    base_rate:    data.base_rate ? String(data.base_rate) : "",
    notes:        "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setBusy(true);
    try {
      const payload = { ...form, lat: gps?.lat, lng: gps?.lng };
      if (type === "shop") {
        await api.put(`/agent/shops/${id}`, payload);
      } else {
        await api.put(`/agent/providers/${type}/${id}`, payload);
      }
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      <BackBtn onClick={onBack} />
      <div className="flex items-center gap-3 mb-5">
        <div className={`${vInfo.color} rounded-xl p-2`}><vInfo.icon className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display text-lg font-bold text-secondary">Modifier {vInfo.label}</h2>
          <p className="text-xs text-muted-foreground">Mettez à jour toutes les informations</p>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Nom *" value={form.name} onChange={v => set("name", v)} />
        <Field label="Description" value={form.description} onChange={v => set("description", v)} multiline />

        {type === "restaurant" && <>
          <Field label="Cuisine" value={form.cuisine} onChange={v => set("cuisine", v)} placeholder="Ivoirienne, Française…" />
          <FieldSelect label="Gamme de prix" value={form.price_range} onChange={v => set("price_range", v)} options={PRICE_RANGES} />
          <Field label="Horaires" value={form.hours} onChange={v => set("hours", v)} placeholder="Lun-Dim 10h-23h" />
        </>}

        {type === "hebergement" && <>
          <FieldSelect label="Type" value={form.htype} onChange={v => set("htype", v)} options={HEBERGEMENT_TYPES} />
          <Field label="Email" value={form.email} onChange={v => set("email", v)} type="email" />
          <Field label="Prix à partir de (FCFA/nuit)" value={form.price_from} onChange={v => set("price_from", v)} type="number" />
          <Field label="Équipements (séparés par virgule)" value={form.amenities} onChange={v => set("amenities", v)} placeholder="wifi, piscine, parking…" />
        </>}

        {type === "transport" && <>
          <FieldSelect label="Type" value={form.ttype} onChange={v => set("ttype", v)} options={TRANSPORT_TYPES} />
          <Field label="Tarif de base (FCFA)" value={form.tarif_base} onChange={v => set("tarif_base", v)} type="number" />
          <Field label="Tarif au km (FCFA)" value={form.tarif_km} onChange={v => set("tarif_km", v)} type="number" />
          <Field label="Zones desservies (séparées par virgule)" value={form.zones} onChange={v => set("zones", v)} />
        </>}

        {type === "provider" && <>
          <Field label="Profession / Domaine" value={form.profession} onChange={v => set("profession", v)} />
          <Field label="Services (séparés par virgule)" value={form.services} onChange={v => set("services", v)} />
          <Field label="Tarif de base (FCFA)" value={form.base_rate} onChange={v => set("base_rate", v)} type="number" />
          <Field label="Zones / Quartiers" value={form.zones} onChange={v => set("zones", v)} />
        </>}

        <Field label="Ville" value={form.city} onChange={v => set("city", v)} />
        <Field label="Adresse" value={form.address} onChange={v => set("address", v)} placeholder="Rue, quartier…" />
        <Field label="Téléphone" value={form.phone} onChange={v => set("phone", v)} type="tel" />
        <Field label="Contact / Responsable" value={form.contact_name} onChange={v => set("contact_name", v)} />
        <Field label="Notes de visite" value={form.notes} onChange={v => set("notes", v)} multiline placeholder="Changements constatés, observations…" />
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 flex gap-3 max-w-lg mx-auto">
        <Button variant="outline" className="flex-1" onClick={onBack} disabled={busy}><X className="w-4 h-4 mr-1" />Annuler</Button>
        <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={submit} disabled={busy}>
          {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Enregistrer
        </Button>
      </div>
    </div>
  );
}

// ─── Composants utilitaires ───────────────────────────────────────────────────

function ActionCard({ icon: Icon, label, color, onClick }) {
  return (
    <button onClick={onClick} className="bg-card border border-border rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-sm hover:border-primary/30 transition-all text-center">
      <div className={`${color} rounded-xl p-2.5`}><Icon className="w-5 h-5 text-white" /></div>
      <span className="text-xs font-medium text-secondary leading-tight">{label}</span>
    </button>
  );
}

function ShopCard({ shop, onClick, showDistance }) {
  return (
    <div onClick={onClick} className="bg-card border border-border rounded-xl p-4 mb-2 flex items-center gap-3 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all">
      <div className="bg-blue-100 rounded-lg p-2 shrink-0"><Store className="w-4 h-4 text-blue-600" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-secondary truncate">{shop.name}</div>
        <div className="text-xs text-muted-foreground">{shop.city}{shop.address ? ` · ${shop.address}` : ""}</div>
        {showDistance && shop.distance_km && <div className="text-xs text-green-600 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{shop.distance_km} km</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </div>
  );
}

function VerticalIcon({ type, big }) {
  const map = { shop: [Store,"bg-blue-100 text-blue-600"], restaurant:[UtensilsCrossed,"bg-orange-100 text-orange-600"],
    hebergement:[BedDouble,"bg-purple-100 text-purple-600"], transport:[Car,"bg-green-100 text-green-600"],
    provider:[Wrench,"bg-teal-100 text-teal-600"] };
  const [Icon, cls] = map[type] || [Store, "bg-muted text-muted-foreground"];
  return <div className={`${cls} rounded-lg ${big?"p-2":"p-1.5"} shrink-0`}><Icon className={big?"w-5 h-5":"w-3.5 h-3.5"} /></div>;
}

function Field({ label, value, onChange, type="text", placeholder="", multiline=false }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground mb-1 block">{label}</label>
      {multiline
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={3}
            className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
        : <Input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} className="h-10" />
      }
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground mb-1 block">{label}</label>
      <select value={value} onChange={e=>onChange(e.target.value)}
        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary capitalize">
        {options.map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
      </select>
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1 text-sm text-primary mb-4 hover:underline">
      <ArrowLeft className="w-4 h-4" />Retour
    </button>
  );
}
